import {
  GetProposalQuery,
  MoveProposalGroupOrLineItemInput,
  ProposalSelectionMode,
} from '@/gql/graphql';
import { produce } from 'immer';

import {
  enforceSingleMultiChoiceWinner,
  isProposalLineItemId,
  ProposalGroupChild,
} from './proposalSectionTree';

type Section = GetProposalQuery['proposal']['sections'][number];
type ItemGroupSection = Extract<
  Section,
  { __typename: 'ProposalItemGroupSection' }
>;

/** A group's direct children plus the selection mode that governs them. */
type GroupSlot = {
  children: ProposalGroupChild[];
  selectionMode: ProposalSelectionMode;
};

/** Every group in the section that can hold children, keyed for lookup. */
const groupSlotsByKey = (section: ItemGroupSection): Map<string, GroupSlot> => {
  const slots = new Map<string, GroupSlot>();
  for (const group of section.groups) {
    slots.set(group.key, {
      children: group.children,
      selectionMode: group.selectionMode,
    });
    for (const child of group.children) {
      if (child.__typename === 'ProposalItemGroup') {
        slots.set(child.key, {
          children: child.children,
          selectionMode: child.selectionMode,
        });
      }
    }
  }
  return slots;
};

/** Mirrors the server's index clamp: append when omitted, else clamp into the
 * target's post-removal bounds. */
const clampIndex = (
  position: number | null | undefined,
  length: number
): number =>
  position == null ? length : Math.min(Math.max(position, 0), length);

/**
 * Mirror of the server's `reconcileChildren`: a child carries a `selected` flag
 * only under an OPTIONAL / MULTI_CHOICE parent, and none under REQUIRED.
 */
const reshapeMovedChild = (
  child: ProposalGroupChild,
  targetSelectionMode: ProposalSelectionMode
): void => {
  if (!('selected' in child)) return;
  child.selected =
    targetSelectionMode === 'REQUIRED' ? null : (child.selected ?? false);
};

const moveLineItem = (
  section: ItemGroupSection,
  proposalLineItemId: string,
  targetGroupKey: string,
  position: number | null | undefined
): void => {
  const slots = groupSlotsByKey(section);
  const target = slots.get(targetGroupKey);
  if (!target) return;

  let moved: ProposalGroupChild | undefined;
  let source: GroupSlot | undefined;
  for (const slot of slots.values()) {
    const index = slot.children.findIndex(
      (child) =>
        child.__typename === 'ProposalGroupLineItem' &&
        isProposalLineItemId(child.proposalLineItem, proposalLineItemId)
    );
    if (index !== -1) {
      [moved] = slot.children.splice(index, 1);
      source = slot;
      break;
    }
  }
  if (!moved || !source) return;

  target.children.splice(
    clampIndex(position, target.children.length),
    0,
    moved
  );
  reshapeMovedChild(moved, target.selectionMode);
  enforceSingleMultiChoiceWinner(target.children, target.selectionMode);
  if (source !== target) {
    enforceSingleMultiChoiceWinner(source.children, source.selectionMode);
  }
};

const moveGroup = (
  section: ItemGroupSection,
  groupKey: string,
  target: MoveProposalGroupOrLineItemInput['target'],
  position: number | null | undefined
): void => {
  if (target.sectionKey != null) {
    // Reorder within the top level. A depth-changing move (sub-group to top
    // level) is rejected by the server, so it is left unapplied here.
    const index = section.groups.findIndex((group) => group.key === groupKey);
    if (index === -1) return;
    const [moved] = section.groups.splice(index, 1);
    section.groups.splice(
      clampIndex(position, section.groups.length),
      0,
      moved
    );
    return;
  }
  if (target.groupKey == null) return;

  // Move a sub-group beneath a top-level parent. A sub-group target would
  // exceed the two-level depth cap, so only top-level targets apply.
  const targetGroup = section.groups.find(
    (group) => group.key === target.groupKey
  );
  if (!targetGroup) return;
  for (const group of section.groups) {
    const index = group.children.findIndex(
      (child) =>
        child.__typename === 'ProposalItemGroup' && child.key === groupKey
    );
    if (index === -1) continue;
    const [moved] = group.children.splice(index, 1);
    targetGroup.children.splice(
      clampIndex(position, targetGroup.children.length),
      0,
      moved
    );
    reshapeMovedChild(moved, targetGroup.selectionMode);
    enforceSingleMultiChoiceWinner(
      targetGroup.children,
      targetGroup.selectionMode
    );
    if (group !== targetGroup) {
      enforceSingleMultiChoiceWinner(group.children, group.selectionMode);
    }
    return;
  }
};

/**
 * Mirrors the server's move: splice the child out of its parent, insert at the
 * clamped target position, then re-settle each affected group's selection shape.
 * Order is array position only, so the local splice matches the server exactly.
 */
export const applyProposalGroupOrLineItemMove = (
  data: GetProposalQuery,
  input: MoveProposalGroupOrLineItemInput
): GetProposalQuery =>
  produce(data, (draft) => {
    for (const section of draft.proposal.sections) {
      if (section.__typename !== 'ProposalItemGroupSection') continue;
      if (input.child.proposalLineItemId != null) {
        if (input.target.groupKey == null) return;
        moveLineItem(
          section,
          input.child.proposalLineItemId,
          input.target.groupKey,
          input.position
        );
      } else if (input.child.groupKey != null) {
        moveGroup(section, input.child.groupKey, input.target, input.position);
      }
      return;
    }
  });
